import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import { RootState } from 'store';
import { toast } from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { LoaderIcon, Sparkles, Upload } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Button } from 'components/ui/button';
import { ImageGenService } from '../services/image-gen.api';
import { useMutation } from '@tanstack/react-query';
import { useUpscaleImageContext } from '../provider';
import useIsMobile from 'hooks/useIsMobile';

const UpscaleHero = () => {
  const isDarkMode = useSelector((state: RootState) => state.theme.isDarkMode);

  return (
    <div className="space-y-4 md:space-y-8">
      <span className="flex items-center gap-2">
        <img
          src={isDarkMode ? '/upscale-dark.png' : '/upscale.png'}
          className="max-h-[120px] md:max-h-[146px] h-full"
          alt="Image Upscaler"
        />
      </span>
      <div className="md:space-y-5 flex flex-col gap-2">
        <h1 className="text-2xl md:text-5xl font-medium text-text-primary lg:leading-[60px]">
          Welcome to <br className="hidden md:block" /> Image Upscaler
        </h1>
        <p className="text-sm lg:text-xl font-normal text-text-primary md:w-[360px] mb-6 lg:mb-0">
          Enhance your images effortlessly with our powerful AI-powered
          upscaling tool. Improve resolution, sharpen details, and bring your
          visuals to life with just a few clicks. Elevate your content with
          stunning high-quality images in no time.
        </p>
      </div>
    </div>
  );
};

const UploadImageForm = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [token, setToken] = useState('');
  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);

  const { selectedImage, setSelectedImage, setGeneratedImage } =
    useUpscaleImageContext();

  const { mutate: upscaleImage, isPending } = useMutation({
    mutationFn: (formData: FormData) =>
      ImageGenService.upscaleImage(formData, token),
    onSuccess: (data) => {
      console.log(data);

      setGeneratedImage(data.data);

      toast.success('Image Upscaled Successfully');
      navigate('/upscale-image/result');
    },
  });

  const [selectedQuality, setSelectedQuality] = useState('medium');

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  const handleFileSelect = (file: File) => {
    if (file) {
      setSelectedImage(file);
    }
  };

  const onSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setRefreshReCaptcha((r) => !r);

    const formData = new FormData();
    if (selectedImage) {
      formData.append('file', selectedImage);
    }

    upscaleImage(formData);
  };

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  }, []);

  return (
    <div className="w-full max-w-[620px]">
      <div className="w-fit mb-4 hidden md:block text-text-primary">
        <p className="uppercase text-xs text-text-secondary">Start</p>
        <p className="text-xl sm:text-2xl font-semibold text-text-primary">
          Start: Select Image
        </p>
      </div>
      <div className="bg-background-main rounded-lg p-4 sm:p-10 space-y-4 max-w-[620px] w-full">
        <div
          className="bg-background border-2 border-dashed h-[200px] sm:h-[267px] border-primary rounded-lg p-4 sm:p-12 text-center cursor-pointer relative"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => document.getElementById('fileInput')?.click()}
        >
          <input
            type="file"
            id="fileInput"
            className="hidden"
            onChange={(e) => {
              if (e.target.files && e.target.files.length > 0) {
                handleFileSelect(e.target.files[0]);
              }
            }}
            accept="image/png, image/jpeg, image/jpg"
          />
          {selectedImage ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <img
                src={URL.createObjectURL(selectedImage)}
                alt="Preview"
                className="max-h-full max-w-full object-contain"
              />
            </div>
          ) : (
            <div className="flex h-full flex-col items-center justify-center">
              <Upload className="mx-auto mb-4 text-primary" size={24} />
              <div className="text-base sm:text-lg mb-2 text-text-primary">
                {isMobile
                  ? 'Tap to choose image'
                  : 'Drag and drop or click to choose image'}
              </div>
              <div className="text-xs sm:text-sm text-text-secondary">
                PNG, JPG, JPEG (UP TO 5MB)
              </div>
            </div>
          )}
        </div>

        <div>
          <label className="text-xs lg:text-sm text-primary">Quality</label>
          <Select value={selectedQuality} onValueChange={setSelectedQuality}>
            <SelectTrigger>
              <SelectValue placeholder="Select Quality" />
            </SelectTrigger>
            <SelectContent className="duration-0">
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="high">High</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="w-full lg:block">
          <Button
            onClick={onSubmit}
            disabled={
              window.location.pathname === '/upscale-image/result' ||
              !selectedImage ||
              isPending
            }
            className="w-full gap-2 mt-2 text-sm disabled:cursor-not-allowed"
            variant={'customPrimary'}
          >
            {isPending ? (
              <div className="animate-spin">
                <LoaderIcon size={20} />
              </div>
            ) : (
              <Sparkles size={20} />
            )}

            {isPending ? 'Upscaling Image' : 'Upscale Image'}
          </Button>
        </div>

        <GoogleReCaptcha
          action="KaiRecaptcha0911"
          onVerify={setToken}
          refreshReCaptcha={refreshReCaptcha}
        />
      </div>
    </div>
  );
};

export default function GetStartedPage() {
  return (
    <div className="w-full bg-background-main rounded-lg rounded-b-none">
      <div className="mx-auto py-4 lg:pt-[60px] lg:pb-[72px] flex flex-col justify-center items-center px-4 lg:px-11 w-full feature-gradient-bg">
        <div className="flex flex-col md:flex-row md:justify-between max-w-[1260px] mx-auto w-full">
          <UpscaleHero />
          <UploadImageForm />
        </div>
      </div>
    </div>
  );
}
